// ── Pitwall API types ────────────────────────────────────────────────────────
export interface EventListItem {
  event_id: string;
  event_name: string;
  event_date: string | null;
  source: string;
  result_count: number;
}

export interface EventListResponse {
  count: number;
  events: EventListItem[];
}

export interface EventResultEntry {
  result_id: string;
  position: number | null;
  status: string;
  class_name: string | null;
  racer_name: string;
  racer_id: string | null;
  navigator_name: string | null;
  navigator_id: string | null;
  time_text: string | null;
  speed_text: string | null;
  disqualification_reason: string | null;
}

export interface EventDetailResponse {
  event_id: string;
  event_name: string;
  event_date: string | null;
  source: string;
  location: string | null;
  result_count: number;
  results: EventResultEntry[];
}

/** Public site promo modal payload from GET /api/store/promo-modal */
export type SitePromoModalPayload =
  | { active: false }
  | {
      active: true;
      imageUrl: string;
      title: string;
      campaignKey: string;
    };

// Use relative URLs in production, environment-configured URL in development
const API_URL = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || '');

// PitWall API runs on a separate service (port 8000 in dev, same origin in prod)
export const PITWALL_URL = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_PITWALL_URL || 'http://localhost:8000');

export const pitwallApi = {
  getEvents: async (params?: { source?: string; year?: number; limit?: number; offset?: number }) => {
    const p = new URLSearchParams();
    if (params?.source) p.set('source', params.source);
    if (params?.year) p.set('year', String(params.year));
    if (params?.limit) p.set('limit', String(params.limit));
    if (params?.offset) p.set('offset', String(params.offset));
    const res = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/events?${p}`);
    if (!res.ok) throw new Error('Failed to load events');
    return res.json() as Promise<EventListResponse>;
  },
  getEvent: async (eventId: string) => {
    const res = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/events/${eventId}`);
    if (!res.ok) throw new Error('Event not found');
    return res.json() as Promise<EventDetailResponse>;
  },
  getRacer: async (racerId: string) => {
    const res = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/racers/${racerId}`);
    if (!res.ok) throw new Error('Racer not found');
    return res.json();
  },
};

const headers = {
  'Content-Type': 'application/json',
};

// Add auth token to headers if it exists
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
};

// Helper function to handle API responses
const handleResponse = async (response: Response, requiresAuth = false) => {
  console.log('API Response:', {
    url: response.url,
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(response.headers.entries())
  });

  let responseData;
  try {
    const text = await response.text();
    console.log('Raw response text:', text);
    
    // Check if the response is HTML (indicating a server error page)
    if (text.trim().startsWith('<!DOCTYPE html>') || text.trim().startsWith('<html>')) {
      // Check if it's a backend service issue
      if (response.status === 502 || response.status === 504) {
        throw new Error('Backend service is not responding. Please try again later.');
      }
      // Check if it's a server error
      if (response.status === 500) {
        throw new Error('Server encountered an error. Please try again later.');
      }
      // Check if it's a not found error
      if (response.status === 404) {
        throw new Error('The requested resource was not found.');
      }
      // Generic HTML response error
      throw new Error('Received unexpected HTML response. The server might be experiencing issues.');
    }
    
    try {
      responseData = text ? JSON.parse(text) : null;
      console.log('Parsed response data:', responseData);
    } catch (e) {
      console.error('Failed to parse JSON response:', e);
      throw new Error('Invalid server response format. Please check if the backend service is running.');
    }
  } catch (e) {
    console.error('Failed to read response:', e);
    if (e instanceof Error) {
      throw e;
    }
    throw new Error('Failed to process server response. Please check your network connection.');
  }

  if (!response.ok) {
    console.error('API Error Response:', responseData);

    // Only handle 401s for authenticated routes
    if (response.status === 401 && requiresAuth) {
      localStorage.removeItem('token');
      throw new Error(responseData?.message || 'Session expired. Please log in again.');
    }

    if (response.status === 403) {
      throw new Error(responseData?.message || 'Access forbidden');
    }

    // Handle specific error cases
    switch (response.status) {
      case 400:
        throw new Error(responseData?.message || 'Invalid request. Please check your input.');
      case 404:
        throw new Error(responseData?.message || 'The requested resource was not found.');
      case 429:
        throw new Error(responseData?.message || 'Too many requests. Please try again later.');
      case 502:
      case 504:
        throw new Error(responseData?.message || 'Backend service is not responding. Please try again later.');
      default:
        throw new Error(responseData?.message || `Request failed with status ${response.status}. Please try again.`);
    }
  }

  // Return null for 204 No Content responses
  if (response.status === 204) {
    return null;
  }

  console.log('API Success Response:', responseData);
  return responseData;
};

// API endpoints
export const api = {
  // Health check
  checkHealth: async () => {
    try {
      const response = await fetch(`${API_URL}/api/health`);
      const data = await response.json();
      return data.status === 'healthy';
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  },

  // Auth
  auth: {
    login: async (email: string, password: string) => {
      try {
        const response = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });
        
        return handleResponse(response, true);
      } catch (error) {
        console.error('Login network error:', error);
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Network error. Please check your connection and try again.');
      }
    },

    signupToken: async (): Promise<string> => {
      const response = await fetch(`${API_URL}/api/auth/signup-token`);
      const data = await handleResponse(response, true);
      return data.token;
    },

    register: async (data: { 
      email: string; 
      password: string; 
      firstName: string; 
      lastName: string; 
      signupToken: string;
      website: string;
    }) => {
      try {
        const response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });
        
        return handleResponse(response, true);
      } catch (error) {
        console.error('Registration network error:', error);
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Network error. Please check your connection and try again.');
      }
    },

    me: async () => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return handleResponse(response, true);
    },

    updateProfile: async (data: any) => {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response, true);
    },
    uploadAvatar: async (formData: FormData) => {
      const response = await fetch(`${API_URL}/api/auth/upload-avatar`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders().Authorization,
        },
        body: formData
      });
      return handleResponse(response, true);
    },
  },

  // Products (public routes)
  products: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/api/store/products`);
      return handleResponse(response, false);
    },
    getById: async (id: string) => {
      const response = await fetch(`${API_URL}/api/store/products/${id}`);
      return handleResponse(response, false);
    },
    getFeatured: async () => {
      const response = await fetch(`${API_URL}/api/store/products/featured`);
      return handleResponse(response, false);
    },
    getPromoModal: async (): Promise<SitePromoModalPayload> => {
      const response = await fetch(`${API_URL}/api/store/promo-modal`);
      return handleResponse(response, false);
    },
  },

  // Racers (public routes)
  racers: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/api/racers`);
      return handleResponse(response, false);
    },
    getById: async (id: string) => {
      const response = await fetch(`${API_URL}/api/racers/${id}`);
      return handleResponse(response, false);
    },
  },

  // Admin (all protected routes)
  admin: {
    // Dashboard Stats
    getStats: async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/dashboard/stats`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Failed to fetch dashboard stats');
      }
    },


    // Products Management
    getProducts: async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/products`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      } catch (error) {
        throw new Error('Failed to fetch products');
      }
    },

    createProduct: async (data: any) => {
      try {
        const response = await fetch(`${API_URL}/api/admin/products`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response, true);
      } catch (error) {
        throw new Error('Failed to create product');
      }
    },

    updateProduct: async (id: string, data: any) => {
      try {
        const response = await fetch(`${API_URL}/api/admin/products/${id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response, true);
      } catch (error) {
        throw new Error('Failed to update product');
      }
    },

    deleteProduct: async (id: string) => {
      try {
        const response = await fetch(`${API_URL}/api/admin/products/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      } catch (error) {
        throw new Error('Failed to delete product');
      }
    },

    uploadProductImage: async (formData: FormData) => {
      try {
        const response = await fetch(`${API_URL}/api/admin/products/upload-image`, {
          method: 'POST',
          headers: {
            'Authorization': getAuthHeaders().Authorization
          },
          body: formData
        });
        const data = await handleResponse(response, true);
        return data;
      } catch (error) {
        console.error('Error uploading product image:', error);
        throw error;
      }
    },

    uploadPortfolioImages: async (formData: FormData) => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/admin/portfolio/upload-images`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: formData
        });
        return handleResponse(response, true);
      } catch (error) {
        throw new Error('Failed to upload portfolio images');
      }
    },

    // Racers Management
    getRacers: async () => {
      const response = await fetch(`${API_URL}/api/admin/racers`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    createRacer: async (data: any) => {
      const response = await fetch(`${API_URL}/api/admin/racers`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    updateRacer: async (id: string, data: any) => {
      const response = await fetch(`${API_URL}/api/admin/racers/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    deleteRacer: async (id: string) => {
      const response = await fetch(`${API_URL}/api/admin/racers/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Users Management
    getUsers: async () => {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    createUser: async (data: any) => {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    updateUser: async (id: string, data: any) => {
      const response = await fetch(`${API_URL}/api/admin/users/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    deleteUser: async (id: string) => {
      const response = await fetch(`${API_URL}/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Settings
    getSettings: async () => {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    updateSettings: async (data: any) => {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    // Trading Cards
    getTradingCards: async () => {
      const response = await fetch(`${API_URL}/api/admin/trading-cards`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    createTradingCard: async (data: any) => {
      const response = await fetch(`${API_URL}/api/admin/trading-cards`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    updateTradingCard: async (id: string, data: any) => {
      const response = await fetch(`${API_URL}/api/admin/trading-cards/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    deleteTradingCard: async (id: string) => {
      const response = await fetch(`${API_URL}/api/admin/trading-cards/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Scraping Management
    scraping: {
      getStatus: async () => {
        const response = await fetch(`${API_URL}/api/admin/scraping/status`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      },
      getRecentEvents: async () => {
        const response = await fetch(`${API_URL}/api/admin/scraping/recent-events`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      },
      getConfig: async () => {
        const response = await fetch(`${API_URL}/api/admin/scraping/config`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      },
      updateConfig: async (config: any) => {
        const response = await fetch(`${API_URL}/api/admin/scraping/config`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(config)
        });
        return handleResponse(response);
      },
      start: async () => {
        const response = await fetch(`${API_URL}/api/admin/scraping/start`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      },
      stop: async () => {
        const response = await fetch(`${API_URL}/api/admin/scraping/stop`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      }
    },

    pitwall: {
      checkHealth: async () => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/health`);
        return handleResponse(response, false);
      },
      bootstrapSources: async () => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/sources/bootstrap`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      },
      runManualScrape: async (source: 'SSCC' | 'BBOR' | 'SORC', payload: { event_url?: string | null; limit: number }) => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/scrape/${source}/manual`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
        return handleResponse(response, true);
      },
      getRuns: async (source?: 'SSCC' | 'BBOR' | 'SORC', limit = 25) => {
        const params = new URLSearchParams();
        if (source) params.append('source', source);
        params.append('limit', String(limit));
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/scrape/runs?${params.toString()}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      },
      getRunDetails: async (runId: string) => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/scrape/runs/${runId}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      },
      getWomenReferences: async (options?: { q?: string; onlyActive?: boolean; limit?: number }) => {
        const params = new URLSearchParams();
        if (options?.q) params.append('q', options.q);
        if (options?.onlyActive) params.append('only_active', 'true');
        if (options?.limit) params.append('limit', String(options.limit));
        const query = params.toString();
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/women-reference${query ? `?${query}` : ''}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      },
      createWomenReference: async (payload: {
        source_system: string;
        external_id: string;
        canonical_name: string;
        alias_names: string[];
        notes?: string | null;
        is_active?: boolean;
        metadata?: Record<string, any>;
      }) => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/women-reference`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
        return handleResponse(response, true);
      },
      updateWomenReference: async (
        referenceId: string,
        payload: {
          canonical_name?: string;
          alias_names?: string[];
          notes?: string | null;
          is_active?: boolean;
          metadata?: Record<string, any>;
        }
      ) => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/women-reference/${referenceId}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
        return handleResponse(response, true);
      },
      deleteWomenReference: async (referenceId: string) => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/women-reference/${referenceId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      },
      searchRacers: async (
        query: string,
        options?: { statusFilter?: 'FINISHED' | 'DQ' | 'DNF' | 'DNS' | 'NT'; limit?: number }
      ) => {
        const params = new URLSearchParams();
        params.append('q', query);
        if (options?.statusFilter) params.append('status_filter', options.statusFilter);
        if (options?.limit) params.append('limit', String(options.limit));
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/racers/search?${params.toString()}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      },
      updateRacerPhoto: async (racerId: string, photoUrl: string | null) => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/racers/${racerId}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ photo_url: photoUrl }),
        });
        return handleResponse(response, true);
      },
      uploadRacerPhoto: async (racerId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const { 'Content-Type': _ct, ...headers } = getAuthHeaders();
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/racers/${racerId}/photo`, {
          method: 'POST',
          headers,
          body: formData,
        });
        return handleResponse(response, true);
      },
      mergeRacers: async (canonicalId: string, sourceIds: string[], canonicalName?: string) => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/racers/${canonicalId}/merge`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ source_racer_ids: sourceIds, canonical_name: canonicalName ?? null }),
        });
        return handleResponse(response, true);
      },
      getHiddenRacers: async () => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/hidden-racers`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      },
      setRacerHidden: async (racerId: string, hidden: boolean, note?: string | null) => {
        const response = await fetch(`${PITWALL_URL}/pitwall-api/pitwall/admin/racers/${racerId}/hidden`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ hidden, note: note ?? null }),
        });
        return handleResponse(response, true);
      },
    },
  },

  // Design Services
  design: {
    // Public routes
    getPortfolioItems: async (category?: string) => {
      try {
        const url = new URL('/api/design/portfolio', window.location.origin);
        if (category && category !== 'all') {
          url.searchParams.append('category', category);
        }
        const response = await fetch(url);
        return handleResponse(response, false);
      } catch (error) {
        console.error('Error fetching portfolio items:', error);
        throw new Error('Failed to fetch portfolio items');
      }
    },

    // Protected routes
    submitQuoteRequest: async (formData: FormData) => {
      try {
        const response = await fetch(`${API_URL}/api/design/quote-request`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            // Don't set Content-Type, let the browser set it with the boundary
          },
          body: formData
        });
        return handleResponse(response, true);
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Failed to submit quote request');
      }
    },

    getProjects: async () => {
      try {
        const response = await fetch(`${API_URL}/api/design/projects`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Failed to fetch projects');
      }
    },

    getProjectById: async (projectId: number) => {
      try {
        const response = await fetch(`${API_URL}/api/design/projects/${projectId}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Failed to fetch project');
      }
    },

    // Admin methods
    getAllProjects: async (options: { status?: string; type?: string; page?: number; limit?: number } = {}) => {
      try {
        const params = new URLSearchParams();
        if (options.status) params.append('status', options.status);
        if (options.type) params.append('type', options.type);
        if (options.page) params.append('page', options.page.toString());
        if (options.limit) params.append('limit', options.limit.toString());

        const url = `${API_URL}/api/design/admin/projects?${params}`;
        console.log('Fetching projects from:', url);
        console.log('Auth headers:', getAuthHeaders());

        const response = await fetch(url, {
          headers: getAuthHeaders()
        });
        return handleResponse(response, true);
      } catch (error) {
        console.error('Error in getAllProjects:', error);
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Failed to fetch all projects');
      }
    },

    updateProject: async (projectId: string, data: any) => {
      try {
        const response = await fetch(`${API_URL}/api/design/admin/projects/${projectId}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        });
        return handleResponse(response, true);
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Failed to update project');
      }
    },

    uploadFiles: async (files: File | File[], type: string = 'general') => {
      try {
        const formData = new FormData();
        if (Array.isArray(files)) {
          files.forEach(file => formData.append('files', file));
        } else {
          formData.append('files', files);
        }
        
        const response = await fetch(`${API_URL}/api/design/upload?type=${type}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        const data = await handleResponse(response, true);
        return data.urls;
      } catch (error) {
        console.error('Error uploading files:', error);
        throw error;
      }
    },

    createPortfolioItem: async (data: {
      title: string;
      category: string;
      description: string;
      images: string[];
    }) => {
      const response = await fetch(`${API_URL}/api/design/admin/portfolio`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      return handleResponse(response, true);
    },

    deletePortfolioItem: async (id: string) => {
      const response = await fetch(`${API_URL}/api/design/portfolio/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handleResponse(response, true);
    },
  },

  // Portfolio (all protected routes)
  portfolio: {
    getItems: async () => {
      const response = await fetch(`${API_URL}/api/design/portfolio`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response, true);
    },

    createItem: async (data: any) => {
      const response = await fetch(`${API_URL}/api/design/admin/portfolio`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      return handleResponse(response, true);
    },

    updateItem: async (id: string, data: any) => {
      const response = await fetch(`${API_URL}/api/design/admin/portfolio/${id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      return handleResponse(response, true);
    },

    deleteItem: async (id: string) => {
      const response = await fetch(`${API_URL}/api/design/admin/portfolio/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handleResponse(response, true);
    },

    uploadImages: async (formData: FormData) => {
      const response = await fetch(`${API_URL}/api/design/admin/portfolio/upload-images`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
        },
        body: formData,
      });
      return handleResponse(response, true);
    },
  },

  user: {
    getPurchaseHistory: async () => {
      const response = await fetch(`${API_URL}/api/user/purchase-history`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response, true);
    },
  },
}; 